import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Label,
  Row,
  Button,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
  Badge,
} from "reactstrap";
import Select from "react-select";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { getCustomers } from "../../components/Api/GmsCustomer";
import { getEmployees } from "../../components/Api/GmsEmployee";
import { getBookings, updateCompletionStatus, updateCompletionStatusWithTime, updateBulkCompletionStatus } from "../../components/Api/GmsBooking";
import { getRooms } from "../../components/Api/GmsRoom";
import { getSpaServices } from "../../components/Api/GmsSpaService";
import { getSpaPackages } from "../../components/Api/GmsSpaPackage";
import { getSpaPackageSeries } from "../../components/Api/GmsSpaPackageSeries";
import { getCustomerMemberships } from "../../components/Api/GmsCustomerMembership";
import { Calendar, Filter, Eye, Plus, Clock, User, MapPin } from "lucide-react";
import {
  hasPagePermissionWithAdmin,
  hasViewOnlyPermission,
  hasEditOrHigherPermission,
  isUserAdmin
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { useNavigate } from "react-router-dom";
import BASE_URL from "../../config";

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

const SpaCalendarPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [showFilters, setShowFilters] = useState(false);
  const [calendarData, setCalendarData] = useState({});

  // New state for popup modal
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dateBookings, setDateBookings] = useState([]);
  const [originalDateBookings, setOriginalDateBookings] = useState([]); // Store original bookings for filter reset
  const [modalLoading, setModalLoading] = useState(false);
  const [filteredEmployeeId, setFilteredEmployeeId] = useState(null); // Track employee filter
  const [filteredEmployeeName, setFilteredEmployeeName] = useState(null); // Track employee name for display

  // New state for individual booking details modal
  const [showIndividualBookingModal, setShowIndividualBookingModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  // State for current time (updates every minute)
  const [currentTime, setCurrentTime] = useState(new Date());
  const scheduleHeaderRef = useRef(null);

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsSpaCalendar');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsSpaCalendar');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsSpaCalendar');
  const isAdmin = isUserAdmin();

  // Get current user's employee ID for filtering
  // Check multiple sources: user object, authUser object, and direct localStorage
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const authUser = JSON.parse(localStorage.getItem('authUser') || '{}');
  const currentEmployeeId = currentUser.user_EmployeeId || currentUser.employeeId || authUser.employeeId || localStorage.getItem('employeeId');

  // Format date as local datetime string (YYYY-MM-DDTHH:mm:ss) without timezone conversion
  const formatLocalDateTime = (date) => {
    try {
      if (!date) return null;
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      const seconds = String(d.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    } catch (error) {
      console.error('Error formatting local datetime:', error);
      return null;
    }
  };

  useEffect(() => {
    if (hasPermission) {
      loadInitialData();
      // Ensure we start with the current month
      const currentDate = new Date();
      setSelectedMonth(currentDate);
    }
  }, [hasPermission]);

  // Debug: Log the selected month when it changes
  useEffect(() => {
  }, [selectedMonth]);

  useEffect(() => {
    if (hasPermission) {
      loadBookings();
    }
  }, [selectedMonth, selectedCustomer, selectedEmployee, hasPermission]);

  const loadInitialData = async () => {
    try {
      await Promise.all([
        loadCustomers(),
        loadEmployees(),
        loadRooms(),
        loadSpaServices(),
        loadSpaPackages(),
        loadSpaPackageSeries(),
        loadMemberships(),
      ]);
    } catch (error) {
      console.error("Error loading initial data:", error);
      Swal.fire("Error", "Failed to load initial data", "error");
    }
  };

  const loadRooms = async () => {
    try {
      const data = await getRooms({});
      // Store room data globally for entity name mapping
      window.roomData = data;
    } catch (error) {
      console.error('Error loading rooms:', error);
      // Don't show error to user as this is not critical for basic functionality
    }
  };

  const loadCustomers = async () => {
    try {
      const data = await getCustomers({});
      const formattedCustomers = data.map(customer => ({
        value: customer.customer_Id,
        label: customer.customer_NameEn || customer.customer_Name,
      }));
      setCustomers(formattedCustomers);
    } catch (error) {
      console.error('Error loading customers:', error);
      Swal.fire("Error", "Failed to load customers", "error");
    }
  };

  const loadEmployees = async () => {
    try {
      const data = await getEmployees({});
      const formattedEmployees = data.map(emp => ({
        value: emp.employee_Id,
        label: emp.employee_NameEn || emp.employee_Name,
      }));
      setEmployees(formattedEmployees);
      // Store employee data globally for entity name mapping
      window.employeeData = data;
    } catch (error) {
      console.error('Error loading employees:', error);
      Swal.fire("Error", "Failed to load employees", "error");
    }
  };

  const loadSpaServices = async () => {
    try {
      const data = await getSpaServices({});
      // Store services data for entity name mapping
      window.spaServicesData = data;
    } catch (error) {
      console.error('Error loading spa services:', error);
      // Don't show error to user as this is not critical for basic functionality
    }
  };

  const loadSpaPackages = async () => {
    try {
      const data = await getSpaPackages({});
      // Store packages data for entity name mapping
      window.spaPackagesData = data;
    } catch (error) {
      console.error('Error loading spa packages:', error);
      // Don't show error to user as this is not critical for basic functionality
    }
  };

  const loadSpaPackageSeries = async () => {
    try {
      const data = await getSpaPackageSeries({});
      // Store package series data for entity name mapping
      window.spaPackageSeriesData = data;
    } catch (error) {
      console.error('Error loading spa package series:', error);
      // Don't show error to user as this is not critical for basic functionality
    }
  };

  const loadMemberships = async () => {
    try {
      const data = await getCustomerMemberships({});
      // Store memberships data for entity name mapping
      window.membershipsData = data;
    } catch (error) {
      console.error('Error loading memberships:', error);
      // Don't show error to user as this is not critical for basic functionality
    }
  };

  const loadBookings = async () => {
    try {
      setLoading(true);

      // Get the first and last day of the selected month
      const year = selectedMonth.getFullYear();
      const month = selectedMonth.getMonth();

      // Create dates in local timezone to avoid timezone issues
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);

      // Format dates as YYYY-MM-DD in local timezone
      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const filterData = {
        from_Date: formatDate(firstDay),
        to_Date: formatDate(lastDay),
        customer_Id: selectedCustomer?.value || null,
        employee_Id: selectedEmployee?.value || null,
        page: 1,
        pageSize: 10000, // Large page size to get all bookings for the month
      };

      // Debug logging

      // For view-only users with employee ID, force filter by their employee ID
      // This ensures they only see their own bookings
      if (isViewOnly && currentEmployeeId) {
        filterData.employee_Id = currentEmployeeId;
        // Clear any other employee filter that might have been set
        if (selectedEmployee?.value !== currentEmployeeId) {
          filterData.employee_Id = currentEmployeeId;
        }
      }

      const response = await getBookings(filterData);

      // Handle paginated response from backend
      let bookingsData = [];
      if (response && Array.isArray(response.bookings)) {
        // New paginated response format
        bookingsData = response.bookings;
      } else if (Array.isArray(response)) {
        // Fallback for non-paginated response (backward compatibility)
        bookingsData = response;
      } else if (response && Array.isArray(response.data)) {
        // Some backends wrap the array in a data property
        bookingsData = response.data;
      } else {
        console.error("Error loading bookings - unexpected response format:", response);
        bookingsData = [];
      }

      setBookings(bookingsData);

      // Process calendar data
      processCalendarData(bookingsData, year, month);
    } catch (error) {
      console.error("Error loading bookings:", error);
      Swal.fire("Error", "Failed to load bookings", "error");
    } finally {
      setLoading(false);
    }
  };

  const processCalendarData = (bookingsData, year, month) => {
    const calendar = {};

    // Initialize all days of the month
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    for (let day = 1; day <= daysInMonth; day++) {
      const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      calendar[dateKey] = 0;
    }

    // Count bookings for each date
    const safeBookings = Array.isArray(bookingsData) ? bookingsData : [];
    safeBookings.forEach(booking => {
      if (booking.booking_Date) {
        const bookingDate = new Date(booking.booking_Date);
        // Format the date to match our calendar keys
        const dateKey = `${bookingDate.getFullYear()}-${String(bookingDate.getMonth() + 1).padStart(2, '0')}-${String(bookingDate.getDate()).padStart(2, '0')}`;
        if (calendar[dateKey] !== undefined) {
          calendar[dateKey]++;
        }
      }
    });

    setCalendarData(calendar);
  };

  const handleMonthChange = (date) => {
    setSelectedMonth(date);
  };

  const handleApplyFilter = () => {
    loadBookings();
  };

  const handleClearFilter = () => {
    setSelectedCustomer(null);
    setSelectedEmployee(null);
    const currentDate = new Date();
    setSelectedMonth(currentDate);
  };

  const handleAddBooking = (dateKey) => {
    // Navigate to add booking page with the selected date
    navigate(`/GmsAddBooking?date=${dateKey}`, { state: { fromSpaCalendar: true } });
  };

  const handleShowIndividualBooking = (booking) => {
    setSelectedBooking(booking);
    setShowIndividualBookingModal(true);
  };

  const handleDateClick = async (dateKey) => {
    try {
      setSelectedDate(dateKey);
      setModalLoading(true);
      setShowBookingModal(true);
      // Clear any previous employee filter when opening date modal
      setFilteredEmployeeId(null);
      setFilteredEmployeeName(null);

      // Parse the clicked date correctly - use local date to avoid timezone issues
      const [yearStr, monthStr, dayStr] = dateKey.split('-');
      const year = parseInt(yearStr);
      const month = parseInt(monthStr) - 1; // Month is 0-indexed
      const day = parseInt(dayStr);

      // Create date in local timezone and format as YYYY-MM-DD
      const formattedDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;


      const filterData = {
        from_Date: formattedDate,
        to_Date: formattedDate,
        customer_Id: selectedCustomer?.value || null,
        employee_Id: selectedEmployee?.value || null,
        page: 1,
        pageSize: 1000, // Large page size to get all bookings for the day
      };

      // For view-only users with employee ID, force filter by their employee ID
      // This ensures they only see their own bookings
      if (isViewOnly && currentEmployeeId) {
        filterData.employee_Id = currentEmployeeId;
        // Clear any other employee filter that might have been set
        if (selectedEmployee?.value !== currentEmployeeId) {
          filterData.employee_Id = currentEmployeeId;
        }
      }


      const response = await getBookings(filterData);

      // Handle paginated response from backend
      let data = [];
      if (response && Array.isArray(response.bookings)) {
        // New paginated response format
        data = response.bookings;
      } else if (Array.isArray(response)) {
        // Fallback for non-paginated response (backward compatibility)
        data = response;
      } else if (response && Array.isArray(response.data)) {
        // Some backends wrap the array in a data property
        data = response.data;
      } else {
        console.error("Error loading bookings for date click - unexpected response format:", response);
        data = [];
      }

      if (data && data.length > 0) {
        // Extract unique IDs from bookings
        const customerIds = [...new Set(data.map(b => b.customer_Id))];
        const employeeIds = [...new Set(data.map(b => b.employee_Id))];
        const roomIds = [...new Set(data.map(b => b.room_Id))];

        // Extract entity IDs for services, packages, and package series
        const serviceIds = [];
        const packageIds = [];
        const packageSeriesIds = [];

        data.forEach(booking => {
          if (booking.details) {
            booking.details.forEach(detail => {
              if (detail.entity_Type === 'Service') {
                serviceIds.push(detail.entity_Id);
              } else if (detail.entity_Type === 'Package') {
                packageIds.push(detail.entity_Id);
              } else if (detail.entity_Type === 'PackageSeries') {
                packageSeriesIds.push(detail.entity_Id);
              }
            });
          }
        });

        // Get names for all IDs using existing APIs
        const [customerData, employeeData, roomData] = await Promise.all([
          getCustomers({}),
          getEmployees({}),
          getRooms({})
        ]);

        // Use already loaded entity data or load if not available
        const serviceData = window.spaServicesData || [];
        const packageData = window.spaPackagesData || [];
        const packageSeriesData = window.spaPackageSeriesData || [];
        const membershipsData = window.membershipsData || [];

        // Entity data loaded successfully

        // Create lookup maps
        const customerMap = {};
        const employeeMap = {};
        const roomMap = {};
        const serviceMap = {};
        const packageMap = {};
        const packageSeriesMap = {};
        const membershipMap = {};

        customerData.forEach(c => customerMap[c.customer_Id] = c.customer_NameEn || c.customer_Name);
        employeeData.forEach(e => employeeMap[e.employee_Id] = e.employee_NameEn || e.employee_Name);
        roomData.forEach(r => roomMap[r.room_Id] = r.room_Name_English || r.room_Name);
        serviceData.forEach(s => serviceMap[s.spaService_Id] = s.spaServiceName_En || s.spaServiceName_Ar);
        packageData.forEach(p => packageMap[p.packageId] = p.packageName_En || p.packageName_Ar);
        packageSeriesData.forEach(ps => packageSeriesMap[ps.seriesId] = ps.seriesName_En || ps.seriesName_Ar);
        membershipsData.forEach(m => membershipMap[m.membershipId] = m);

        // Entity maps created successfully

        // Add names to bookings
        const enrichedBookings = data.map(booking => {
          // Get employee and room names from resource details
          const employeeIds = [...new Set(booking.details?.flatMap(d => d.resourceDetails?.map(r => r.employee_Id).filter(Boolean) || []) || [])];
          const roomIds = [...new Set(booking.details?.flatMap(d => d.resourceDetails?.map(r => r.room_Id).filter(Boolean) || []) || [])];

          // Employee mapping processed

          const employeeNames = employeeIds.map(id => employeeMap[id] || `Employee #${id}`).join(', ');
          const roomNames = roomIds.map(id => roomMap[id] || `Room #${id}`).join(', ');

          return {
            ...booking,
            customer_Name: customerMap[booking.customer_Id] || `Customer #${booking.customer_Id}`,
            employee_Name: employeeNames || 'No Employee',
            room_Name: roomNames || 'No Room',
            details: booking.details ? booking.details.map(detail => {
              let entityName = '';

              if (detail.entity_Type === 'Service' || detail.entity_Type === 'ser') {
                entityName = serviceMap[detail.entity_Id] || `Service #${detail.entity_Id}`;
              } else if (detail.entity_Type === 'Package' || detail.entity_Type === 'pac') {
                entityName = packageMap[detail.entity_Id] || `Package #${detail.entity_Id}`;
              } else if (detail.entity_Type === 'PackageSeries' || detail.entity_Type === 'sep') {
                entityName = packageSeriesMap[detail.entity_Id] || `Package Series #${detail.entity_Id}`;
              } else if (detail.entity_Type === 'mem') {
                // Handle membership using pre-loaded data
                const membership = membershipMap[detail.entity_Id];
                if (membership && membership.entity_Type === 'sep') {
                  // Get package series name
                  const seriesName = packageSeriesMap[membership.entity_Id] || `Package Series #${membership.entity_Id}`;
                  entityName = seriesName;
                } else if (membership && membership.entity_Type === 'pac') {
                  // Get package name
                  const packageName = packageMap[membership.entity_Id] || `Package #${membership.entity_Id}`;
                  entityName = packageName;
                } else {
                  entityName = `Membership #${detail.entity_Id}`;
                }
              } else {
                entityName = `Unknown #${detail.entity_Id}`;
              }

              return {
                ...detail,
                entity_Name: entityName
              };
            }) : []
          };
        });

        setDateBookings(enrichedBookings);
        setOriginalDateBookings(enrichedBookings); // Store original for filter reset
      } else {
        setDateBookings([]);
        setOriginalDateBookings([]);
      }
    } catch (error) {
      console.error("Error loading date bookings:", error);
      Swal.fire("Error", "Failed to load booking details", "error");
    } finally {
      setModalLoading(false);
    }
  };

  const formatTime = (dateTimeStr) => {
    try {
      const date = new Date(dateTimeStr);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return "N/A";
    }
  };

  // Helper function to check if a date is in the past
  const isDateInPast = (dateKey) => {
    const [year, month, day] = dateKey.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    return date < today;
  };

  // Helper function to check if a date is today
  const isDateToday = (dateKey) => {
    const [year, month, day] = dateKey.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const handleScheduleBodyScroll = (e) => {
    if (scheduleHeaderRef.current) {
      scheduleHeaderRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  };

  const getEntityTypeDisplay = (entityType) => {
    switch (entityType) {
      case 'Service':
      case 'ser': return 'Spa Service';
      case 'Package':
      case 'pac': return 'Spa Package';
      case 'PackageSeries':
      case 'sep': return 'Package Series';
      case 'mem': return 'Membership';
      default: return entityType;
    }
  };

  // Grid calendar helper functions
  const generateTimeSlots = (startHour = 8, endHour = 24, intervalMinutes = 30) => {
    const slots = [];
    for (let hour = startHour; hour <= endHour; hour++) {
      for (let minute = 0; minute < 60; minute += intervalMinutes) {
        if (hour === endHour && minute > 0) break;
        const time = new Date();
        time.setHours(hour, minute, 0, 0);
        slots.push({
          hour,
          minute,
          time,
          display: time.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          })
        });
      }
    }
    return slots;
  };

  const getAppointmentColor = (detail, resource, completionStatus) => {
    // Color coding based on resource details and status - matching "Select Billed Services" popup
    const status = completionStatus || 'Pending';

    // Status-based colors (priority - these override entity type colors)
    if (status === 'Complete') return '#a8e6cf'; // Light Green
    if (status === 'Check In') return '#b3e5fc'; // Light Teal/Cyan
    if (status === 'Start') return '#fff9c4'; // Light Yellow
    if (status === 'Stop') return '#e0e0e0'; // Light Gray

    // Entity type colors (used when status is Pending or not set)
    // Logic matches "Select Billed Services" popup:
    // - Orange: Standalone services (no Package_Id, no Membership_Id)
    // - Blue: Services from Packages (has Package_Id, no Membership_Id)
    // - Green: Services from Package Series (Entity_Type = 'sep' OR Membership with Entity_Type = 'SEP')

    // Check if service is from membership
    const membershipId = resource?.membership_Id || resource?.membershipId || resource?.Membership_Id;
    if (membershipId) {
      // Service is from membership - check membership entity type
      const membershipsData = window.membershipsData || [];
      const membership = membershipsData.find(m =>
        m.membershipId === membershipId ||
        m.MembershipId === membershipId ||
        m.membership_Id === membershipId
      );

      if (membership) {
        const membershipEntityType = membership.entity_Type || membership.entityType;
        // Package Series Membership
        if (membershipEntityType === 'SEP' || membershipEntityType === 'sep' || membershipEntityType === 'PackageSeries' || membershipEntityType === 3) {
          return '#4CAF50'; // Green - Package Series Membership
        }
        // Package Membership
        if (membershipEntityType === 'PAC' || membershipEntityType === 'pac' || membershipEntityType === 'Package' || membershipEntityType === 2) {
          return '#2196F3'; // Blue - Package Membership
        }
      }
      // If membership not found or unknown type, default to service color
      return '#FF9800'; // Orange - Standalone Service
    }

    // Check if service is from package (has Package_Id but no Membership_Id)
    const packageId = resource?.package_Id || resource?.packageId || resource?.Package_Id;
    if (packageId && !membershipId) {
      // Check if the detail entity type is Package Series
      const detailEntityType = detail?.entity_Type || detail?.entityType;
      if (detailEntityType === 'sep' || detailEntityType === 'SEP' || detailEntityType === 'PackageSeries') {
        return '#4CAF50'; // Green - Package Series
      }
      // Regular package
      return '#2196F3'; // Blue - Package
    }

    // Check if detail entity type is Package Series (direct booking of package series)
    const detailEntityType = detail?.entity_Type || detail?.entityType;
    if (detailEntityType === 'sep' || detailEntityType === 'SEP' || detailEntityType === 'PackageSeries') {
      return '#4CAF50'; // Green - Package Series
    }

    // Default: Standalone service (no Package_Id, no Membership_Id)
    return '#FF9800'; // Orange - Standalone Service
  };

  // Helper function to determine text color based on background brightness
  const getTextColor = (backgroundColor) => {
    // Vibrant colors (entity types) - use white text
    const vibrantColors = ['#FF9800', '#2196F3', '#4CAF50'];
    if (vibrantColors.includes(backgroundColor)) {
      return 'white';
    }
    // Light pastel colors (status colors) - use dark text
    return '#495057';
  };

  const transformBookingsToGridData = (bookings, filterEmployeeId = null) => {
    const gridItems = [];
    const employeeMap = new Map();
    const unassignedItems = [];

    // Process all bookings and extract resource details
    bookings.forEach(booking => {
      if (!booking.details || !Array.isArray(booking.details)) return;

      booking.details.forEach(detail => {
        if (!detail.resourceDetails || !Array.isArray(detail.resourceDetails)) return;

        detail.resourceDetails.forEach(resource => {
          // If filtering by employee, skip resources not assigned to that employee
          // Compare as numbers to handle type mismatches
          if (filterEmployeeId !== null && filterEmployeeId !== undefined) {
            const resourceEmployeeId = resource.employee_Id ? Number(resource.employee_Id) : null;
            const filterId = Number(filterEmployeeId);
            if (resourceEmployeeId !== filterId) {
              return;
            }
          }
          if (!resource.from_Time || !resource.to_Time) return;

          const fromTime = new Date(resource.from_Time);
          const toTime = new Date(resource.to_Time);

          // Get employee name and image
          let employeeName = 'Unassigned';
          let employeeId = resource.employee_Id;
          let employeeImagePath = null;

          if (employeeId && window.employeeData) {
            const employee = window.employeeData.find(e => e.employee_Id === employeeId);
            if (employee) {
              employeeName = employee.employee_NameEn || employee.employee_Name || `Employee #${employeeId}`;
              employeeImagePath = employee.employee_ImagePath || null;
            } else {
              employeeName = `Employee #${employeeId}`;
            }
          } else if (employeeId) {
          }

          // Get service/entity name
          const entityName = detail.entity_Name || 'Service';

          // Get room name
          let roomName = 'No Room';
          if (resource.room_Id && window.roomData) {
            const room = window.roomData.find(r => r.room_Id === resource.room_Id);
            roomName = room ? (room.room_Name_English || room.room_Name) : `Room #${resource.room_Id}`;
          }

          const gridItem = {
            id: resource.bookingResourceDetail_Id,
            bookingId: booking.booking_Id,
            customerName: booking.customer_Name,
            employeeId: employeeId,
            employeeName: employeeName,
            employeeImagePath: employeeImagePath,
            roomName: roomName,
            entityName: entityName,
            entityType: detail.entity_Type,
            fromTime,
            toTime,
            completionStatus: resource.completionStatus || 'Pending',
            booking: booking,
            detail: detail,
            resource: resource
          };

          if (employeeId) {
            if (!employeeMap.has(employeeId)) {
              employeeMap.set(employeeId, {
                employeeId,
                employeeName,
                items: []
              });
            }
            employeeMap.get(employeeId).items.push(gridItem);
          } else {
            unassignedItems.push(gridItem);
          }
        });
      });
    });

    // Convert map to array and sort items by time
    // Also get employee image path from the first item (all items for same employee should have same image)
    const employees = Array.from(employeeMap.values()).map(emp => {
      const firstItem = emp.items[0];
      const employeeImagePath = firstItem?.employeeImagePath || null;

      // Get employee data to ensure we have the image path
      if (emp.employeeId && window.employeeData && !employeeImagePath) {
        const employee = window.employeeData.find(e => e.employee_Id === emp.employeeId);
        if (employee && employee.employee_ImagePath) {
          return {
            ...emp,
            employeeImagePath: employee.employee_ImagePath,
            items: emp.items.sort((a, b) => a.fromTime - b.fromTime)
          };
        }
      }

      return {
        ...emp,
        employeeImagePath: employeeImagePath,
        items: emp.items.sort((a, b) => a.fromTime - b.fromTime)
      };
    });

    // Add unassigned column if there are unassigned items
    if (unassignedItems.length > 0) {
      employees.push({
        employeeId: null,
        employeeName: 'Unassigned',
        items: unassignedItems.sort((a, b) => a.fromTime - b.fromTime)
      });
    }

    return employees;
  };

  const calculateTimePosition = (time, startHour = 8, slotHeight = 120) => {
    const hours = time.getHours();
    const minutes = time.getMinutes();
    const totalMinutes = (hours - startHour) * 60 + minutes;
    return (totalMinutes / 30) * (slotHeight / 2); // 30-minute intervals
  };

  const calculateDuration = (fromTime, toTime, slotHeight = 120) => {
    const diffMs = toTime - fromTime;
    const diffMinutes = diffMs / (1000 * 60);
    return Math.max((diffMinutes / 30) * (slotHeight / 2), 30); // Minimum 30px height
  };

  // Calculate time from click position in the grid
  const calculateTimeFromClick = (clickY, startHour = 8, slotHeight = 120) => {
    // clickY is relative to the overlay/staff-appointments div (which starts after the header)
    // No need to subtract header height since clickY is already relative to the appointments area
    // Calculate which 30-minute slot was clicked
    const slotIndex = Math.floor(clickY / (slotHeight / 2));
    // Default to start of hour (round down to nearest hour)
    const hours = startHour + Math.floor(slotIndex / 2);
    const minutes = 0; // Always default to start of hour (00 minutes)

    console.log('=== TIME CALCULATION ===');
    console.log('clickY:', clickY);
    console.log('slotHeight:', slotHeight);
    console.log('slotHeight / 2:', slotHeight / 2);
    console.log('slotIndex:', slotIndex);
    console.log('Calculated hours:', hours);
    console.log('Calculated minutes:', minutes, '(always 0 - start of hour)');
    console.log('Result time:', `${hours}:${minutes.toString().padStart(2, '0')}`);

    return { hours, minutes, slotIndex };
  };

  // Handle click on empty time slot
  const handleEmptySlotClick = (e, employee, selectedDate) => {
    // Don't trigger if clicking on an appointment block
    if (e.target.closest('.appointment-block')) {
      return;
    }

    e.stopPropagation();

    const slotHeight = 120;
    const startHour = 8;

    // Get the overlay's bounding rect (currentTarget is the overlay div)
    const overlayRect = e.currentTarget.getBoundingClientRect();
    // clickY is relative to the overlay, which starts at top: 0 of staff-appointments
    // The time slots also start at top: 0 of staff-appointments, so no offset needed
    const clickY = e.clientY - overlayRect.top;

    console.log('=== TIME CALCULATION DEBUG ===');
    console.log('e.clientY:', e.clientY);
    console.log('overlayRect.top:', overlayRect.top);
    console.log('clickY (relative to overlay):', clickY);
    console.log('slotHeight:', slotHeight);
    console.log('startHour:', startHour);

    // Calculate clicked time
    const { hours, minutes, slotIndex } = calculateTimeFromClick(clickY, startHour, slotHeight);

    console.log('Calculated slotIndex:', slotIndex);
    console.log('Calculated hours:', hours, 'minutes:', minutes);

    // Create date object for the clicked time on the selected date
    const [year, month, day] = selectedDate.split('-');
    const clickedTime = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), hours, minutes, 0);

    console.log('=== Empty Slot Click ===');
    console.log('Click Y (relative):', clickY);
    console.log('Calculated hours:', hours, 'minutes:', minutes);
    console.log('Clicked time:', clickedTime);
    console.log('Employee:', employee);
    console.log('Selected date:', selectedDate);

    // Navigate to AddBooking with context
    navigate('/GmsAddBooking', {
      state: {
        fromCalendar: true,
        bookingDate: selectedDate,
        startTime: clickedTime.toISOString(),
        employeeId: employee.employeeId,
        employeeName: employee.employeeName
      }
    });
  };

  // Handle booking-level status update (updates all services in the booking)
  const handleBookingStatusUpdate = async (bookingId, newStatus, booking = null) => {
    try {
      // Find the booking if not provided
      if (!booking) {
        booking = dateBookings.find(b => b.booking_Id === bookingId) || selectedBooking;
      }

      if (!booking) {
        Swal.fire("Error", "Booking not found", "error");
        return;
      }

      // Validate status values for booking-level (Pending, Check In, Complete)
      if (!['Pending', 'Check In', 'Complete'].includes(newStatus)) {
        Swal.fire("Error", "Invalid status for booking-level update", "error");
        return;
      }

      // Show confirmation dialog
      let actionText = '';
      if (newStatus === 'Check In') {
        actionText = 'check in';
      } else if (newStatus === 'Complete') {
        actionText = 'mark as complete';
      } else {
        actionText = 'mark as pending';
      }

      const confirmResult = await Swal.fire({
        title: 'Confirm Status Change',
        text: `Are you sure you want to ${actionText} this booking?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, confirm',
        cancelButtonText: 'Cancel'
      });

      if (!confirmResult.isConfirmed) {
        return; // User cancelled
      }

      // Determine check-in time if status is Check In
      let checkInTime = null;
      if (newStatus === 'Check In') {
        checkInTime = formatLocalDateTime(new Date());
      }

      const result = await updateBulkCompletionStatus(
        bookingId,
        newStatus,
        checkInTime
      );

      if (result.isSuccess) {
        // Update the local state to reflect the change
        setDateBookings(prevBookings =>
          prevBookings.map(b =>
            b.booking_Id === bookingId
              ? { ...b, completionStatus: newStatus }
              : b
          )
        );

        // Also update the selectedBooking state if it exists
        if (selectedBooking && selectedBooking.booking_Id === bookingId) {
          setSelectedBooking(prevBooking => ({
            ...prevBooking,
            completionStatus: newStatus
          }));
        }

        // Reload bookings to get updated data
        await loadBookings();
        if (selectedDate) {
          await handleDateClick(selectedDate);
          // Reapply employee filter if it was active
          if (filteredEmployeeId && originalDateBookings.length > 0) {
            const filteredBookings = originalDateBookings.filter(booking => {
              return booking.details && booking.details.some(detail =>
                detail.resourceDetails && detail.resourceDetails.some(resource =>
                  resource.employee_Id === filteredEmployeeId
                )
              );
            });
            setDateBookings(filteredBookings);
          }
        }

        Swal.fire("Success", result.message, "success");
      } else {
        Swal.fire("Error", result.message, "error");
      }
    } catch (error) {
      console.error("Error updating booking status:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to update booking status";
      Swal.fire("Error", errorMessage, "error");
    }
  };

  const handleCompletionStatusUpdate = async (bookingResourceDetailId, newStatus, resourceDetail = null) => {
    try {
      // If resourceDetail is not provided, find it
      if (!resourceDetail) {
        // Check in dateBookings
        for (const booking of dateBookings) {
          if (booking.details) {
            for (const detail of booking.details) {
              if (detail.resourceDetails) {
                const found = detail.resourceDetails.find(r => r.bookingResourceDetail_Id === bookingResourceDetailId);
                if (found) {
                  resourceDetail = found;
                  break;
                }
              }
            }
          }
          if (resourceDetail) break;
        }

        // If not found in dateBookings, check in selectedBooking
        if (!resourceDetail && selectedBooking) {
          for (const detail of selectedBooking.details || []) {
            if (detail.resourceDetails) {
              const found = detail.resourceDetails.find(r => r.bookingResourceDetail_Id === bookingResourceDetailId);
              if (found) {
                resourceDetail = found;
                break;
              }
            }
          }
        }
      }

      // Validate that employee is assigned before allowing Start/Stop
      // Check In does not require employee assignment
      if ((newStatus === 'Start' || newStatus === 'Stop') && (!resourceDetail || !resourceDetail.employee_Id)) {
        Swal.fire({
          title: 'Cannot Update Status',
          text: 'Staff not assigned. Please assign an employee to this booking before updating status.',
          icon: 'warning',
          confirmButtonText: 'OK'
        });
        return;
      }

      // Show confirmation dialog
      let actionText = '';
      if (newStatus === 'Start') {
        actionText = 'start';
      } else if (newStatus === 'Stop') {
        actionText = 'stop';
      } else if (newStatus === 'Check In') {
        actionText = 'check in';
      } else {
        actionText = 'mark as pending';
      }

      const confirmResult = await Swal.fire({
        title: 'Confirm Status Change',
        text: `Are you sure you want to ${actionText}?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, confirm',
        cancelButtonText: 'Cancel'
      });

      if (!confirmResult.isConfirmed) {
        return; // User cancelled
      }

      // Determine which time to pass based on status (use local time, no timezone conversion)
      let checkInTime = null;
      let startTime = null;
      let stopTime = null;

      if (newStatus === 'Check In') {
        checkInTime = formatLocalDateTime(new Date());
      } else if (newStatus === 'Start') {
        startTime = formatLocalDateTime(new Date());
      } else if (newStatus === 'Stop') {
        stopTime = formatLocalDateTime(new Date());
      }

      const result = await updateCompletionStatusWithTime(
        bookingResourceDetailId,
        newStatus,
        checkInTime,
        startTime,
        stopTime
      );

      if (result.isSuccess) {
        // Update the local state to reflect the change
        setDateBookings(prevBookings =>
          prevBookings.map(booking => ({
            ...booking,
            details: booking.details.map(detail => ({
              ...detail,
              resourceDetails: detail.resourceDetails.map(resource =>
                resource.bookingResourceDetail_Id === bookingResourceDetailId
                  ? { ...resource, completionStatus: newStatus }
                  : resource
              )
            }))
          }))
        );

        // Also update the selectedBooking state if it exists
        if (selectedBooking) {
          setSelectedBooking(prevBooking => ({
            ...prevBooking,
            details: prevBooking.details.map(detail => ({
              ...detail,
              resourceDetails: detail.resourceDetails.map(resource =>
                resource.bookingResourceDetail_Id === bookingResourceDetailId
                  ? { ...resource, completionStatus: newStatus }
                  : resource
              )
            }))
          }));
        }

        Swal.fire("Success", result.message, "success");
      } else {
        // Check if the error is about employee not found
        if (result.message && result.message.includes("Employee ID not found")) {
          Swal.fire({
            title: 'Cannot Mark as Done',
            text: 'Staff not assigned. Please assign an employee to this booking before marking it as complete.',
            icon: 'warning',
            confirmButtonText: 'OK'
          });
        } else {
          Swal.fire("Error", result.message, "error");
        }
      }
    } catch (error) {
      console.error("Error updating completion status:", error);
      // Check if the error response contains employee not found message
      const errorMessage = error.response?.data?.message || error.message || "Failed to update completion status";
      if (errorMessage.includes("Employee ID not found") || errorMessage.includes("Employee ID not found for this user")) {
        Swal.fire({
          title: 'Cannot Mark as Done',
          text: 'Staff not assigned. Please assign an employee to this booking before marking it as complete.',
          icon: 'warning',
          confirmButtonText: 'OK'
        });
      } else {
        Swal.fire("Error", errorMessage, "error");
      }
    }
  };

  const renderBookingModal = () => {
    if (!selectedDate) return null;

    const [year, month, day] = selectedDate.split('-');
    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    const displayDate = `${day} ${monthNames[parseInt(month) - 1]} ${year}`;

    return (
      <Modal
        isOpen={showBookingModal}
        toggle={() => setShowBookingModal(false)}
        size="xl"
        className="booking-details-modal"
        style={{ maxWidth: '95vw', width: '95vw', maxHeight: '95vh', height: '95vh' }}
      >
        <ModalHeader toggle={() => {
          setShowBookingModal(false);
          setFilteredEmployeeId(null);
          setFilteredEmployeeName(null);
        }}>
          <div className="d-flex align-items-center justify-content-between w-100">
            <div className="d-flex align-items-center">
              <Calendar className="me-2" size={20} />
              <span>
                Bookings for {displayDate}
                {filteredEmployeeName && (
                  <span className="ms-2 text-muted" style={{ fontSize: '0.9rem' }}>
                    - {filteredEmployeeName}
                  </span>
                )}
              </span>
            </div>
            {filteredEmployeeId && (
              <Button
                color="link"
                size="sm"
                className="p-0"
                onClick={(e) => {
                  e.stopPropagation();
                  // Restore original bookings
                  if (originalDateBookings.length > 0) {
                    setDateBookings(originalDateBookings);
                  } else if (selectedDate) {
                    // If no original bookings stored, reload
                    handleDateClick(selectedDate);
                  }
                  setFilteredEmployeeId(null);
                  setFilteredEmployeeName(null);
                }}
                title="Show all bookings"
              >
                <small>Show All</small>
              </Button>
            )}
          </div>
        </ModalHeader>
        <ModalBody style={{ padding: 0, maxHeight: '90vh', height: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {modalLoading ? (
            <div className="text-center py-4">
              <ClipLoader size={40} color="#556ee6" />
              <p className="mt-3">Loading booking details...</p>
            </div>
          ) : dateBookings.length === 0 ? (
            <div className="text-center py-4">
              <Calendar className="text-muted" size={48} />
              <h5 className="mt-3 text-muted">No Bookings</h5>
              <p className="text-muted">There are no bookings scheduled for this date.</p>
            </div>
          ) : (
            <div className="daily-schedule-grid-container">
              <div className="mb-3 px-3 pt-3">
                <div className="d-flex justify-content-between align-items-center">
                  <h6 className="text-primary mb-0">
                    <Clock className="me-2" size={16} />
                    Daily Schedule Overview
                  </h6>
                  <div className="d-flex gap-2 align-items-center flex-wrap">
                    <small className="text-muted" style={{ fontSize: '10px' }}>
                      <span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#a8e6cf', borderRadius: '2px', marginRight: '4px', border: '1px solid #81c784' }}></span>
                      Complete
                    </small>
                    <small className="text-muted" style={{ fontSize: '10px' }}>
                      <span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#b3e5fc', borderRadius: '2px', marginRight: '4px', border: '1px solid #81d4fa' }}></span>
                      Check In
                    </small>
                    <small className="text-muted" style={{ fontSize: '10px' }}>
                      <span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#fff9c4', borderRadius: '2px', marginRight: '4px', border: '1px solid #fdd835' }}></span>
                      In Progress
                    </small>
                    <small className="text-muted" style={{ fontSize: '10px' }}>
                      <span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#FF9800', borderRadius: '2px', marginRight: '4px', border: '1px solid #F57C00' }}></span>
                      Service
                    </small>
                    <small className="text-muted" style={{ fontSize: '10px' }}>
                      <span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#2196F3', borderRadius: '2px', marginRight: '4px', border: '1px solid #1976D2' }}></span>
                      Package
                    </small>
                    <small className="text-muted" style={{ fontSize: '10px' }}>
                      <span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#4CAF50', borderRadius: '2px', marginRight: '4px', border: '1px solid #388E3C' }}></span>
                      Series
                    </small>
                  </div>
                </div>
              </div>
              {(() => {
                const gridData = transformBookingsToGridData(dateBookings, filteredEmployeeId);

                const timeSlots = generateTimeSlots(8, 24, 30);
                const startHour = 8;
                const slotHeight = 120;
                const dayStartTime = new Date();
                dayStartTime.setHours(startHour, 0, 0, 0);

                // Check if selected date is today and calculate current time position
                const isToday = selectedDate ? isDateToday(selectedDate) : false;
                let currentTimePosition = null;

                if (isToday) {
                  const now = currentTime; // Use state to trigger re-renders
                  const currentHour = now.getHours();
                  const currentMinute = now.getMinutes();

                  // Only show if current time is within the schedule range (8 AM - 10 PM)
                  if (currentHour >= startHour && currentHour < 24) {
                    currentTimePosition = calculateTimePosition(now, startHour, slotHeight);
                  }
                }

                if (gridData.length === 0) {
                  return (
                    <div className="text-center py-4 px-3">
                      <p className="text-muted">No appointments scheduled</p>
                    </div>
                  );
                }

                return (
                  <div className="schedule-grid-wrapper" style={{ position: 'relative' }}>
                    <div className="schedule-grid-header-scroll" ref={scheduleHeaderRef}>
                      <div className="schedule-grid schedule-grid-header-row">
                        <div className="time-column">
                          <div className="time-header"></div>
                        </div>
                        <div className="staff-columns">
                          {gridData.map((employee, empIndex) => (
                            <div key={`header-${empIndex}`} className="staff-column">
                              <div className="staff-header">
                                <div
                                  className="staff-avatar clickable-avatar"
                                  title={`${employee.employeeName} - ${employee.items.length} appointment${employee.items.length !== 1 ? 's' : ''} - Click to view all bookings`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // Filter by employee - use original bookings and filter at resource level
                                    const bookingsToFilter = originalDateBookings.length > 0 ? originalDateBookings : dateBookings;

                                    // Set the filter - the transform function will handle filtering at resource level
                                    setDateBookings(bookingsToFilter); // Keep all bookings, filter happens in transform
                                    setFilteredEmployeeId(employee.employeeId);
                                    setFilteredEmployeeName(employee.employeeName);
                                    setShowBookingModal(true);
                                  }}
                                >
                                  {employee.employeeImagePath ? (
                                    <>
                                      <img
                                        src={`${BASE_URL}${employee.employeeImagePath.startsWith('/') ? employee.employeeImagePath : '/' + employee.employeeImagePath}`}
                                        alt={employee.employeeName}
                                        onError={(e) => {
                                          // Hide image and show initial on error
                                          e.target.style.display = 'none';
                                          const fallback = e.target.parentElement.querySelector('.avatar-fallback');
                                          if (fallback) fallback.style.display = 'flex';
                                        }}
                                        style={{
                                          width: '100%',
                                          height: '100%',
                                          borderRadius: '50%',
                                          objectFit: 'cover',
                                          position: 'absolute',
                                          top: 0,
                                          left: 0
                                        }}
                                      />
                                      <span
                                        className="avatar-fallback"
                                        style={{
                                          display: 'none',
                                          width: '100%',
                                          height: '100%',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          position: 'absolute',
                                          top: 0,
                                          left: 0
                                        }}
                                      >
                                        {employee.employeeName.charAt(0).toUpperCase()}
                                      </span>
                                    </>
                                  ) : (
                                    <span style={{
                                      display: 'flex',
                                      width: '100%',
                                      height: '100%',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}>
                                      {employee.employeeName.charAt(0).toUpperCase()}
                                    </span>
                                  )}
                                </div>
                                <div
                                  className="staff-name"
                                  title={employee.employeeName}
                                >
                                  {employee.employeeName}
                                </div>
                                <div
                                  className="staff-count"
                                  title={`${employee.items.length} appointment${employee.items.length !== 1 ? 's' : ''} for ${employee.employeeName}`}
                                >
                                  {employee.items.length}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="schedule-grid-body-scroll" onScroll={handleScheduleBodyScroll}>
                      <div className="schedule-grid">
                        {/* Time slots column */}
                        <div className="time-column">
                          <div className="time-slots">
                            {timeSlots.map((slot, index) => (
                              <div key={index} className="time-slot-cell" style={{ height: `${slotHeight / 2}px` }}>
                                {slot.minute === 0 && (
                                  <span className="time-label">{slot.display}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Staff columns */}
                        <div className="staff-columns">
                          {gridData.map((employee, empIndex) => {
                            console.log('Rendering employee column:', empIndex, employee.employeeName);
                            return (
                              <div key={empIndex} className="staff-column">

                                {/* Staff appointments */}
                                <div
                                  className="staff-appointments"
                                  ref={(el) => {
                                    if (el && empIndex === 0) {
                                      console.log('=== STAFF APPOINTMENTS ELEMENT RENDERED ===');
                                      console.log('Element:', el);
                                      console.log('Element style:', window.getComputedStyle(el));
                                      console.log('Element height:', el.offsetHeight);
                                      console.log('Element width:', el.offsetWidth);
                                    }
                                  }}
                                  style={{
                                    position: 'relative',
                                    height: `${(24 - startHour) * slotHeight}px`,
                                    cursor: hasEditOrHigher && !isDateInPast(selectedDate) ? 'pointer' : 'default',
                                    backgroundColor: empIndex === 0 ? 'rgba(255, 0, 0, 0.01)' : 'transparent' // Slight red tint for first column to verify rendering
                                  }}
                                  onMouseEnter={() => {
                                    console.log('=== MOUSE ENTER staff-appointments ===', employee.employeeName);
                                  }}
                                  onMouseLeave={() => {
                                    console.log('=== MOUSE LEAVE staff-appointments ===', employee.employeeName);
                                  }}
                                  onClick={(e) => {
                                    console.log('=== STAFF APPOINTMENTS CLICK ===');
                                    console.log('Click detected on staff-appointments div');
                                    console.log('Event:', e);
                                    console.log('Event target:', e.target);
                                    console.log('Event currentTarget:', e.currentTarget);
                                    console.log('Target classList:', e.target.classList);
                                    console.log('Target tagName:', e.target.tagName);
                                    console.log('Target === currentTarget?', e.target === e.currentTarget);
                                    console.log('Closest appointment-block:', e.target.closest('.appointment-block'));
                                    console.log('hasEditOrHigher:', hasEditOrHigher);
                                    console.log('isDateInPast:', isDateInPast(selectedDate));
                                    console.log('Employee:', employee);
                                    console.log('Selected date:', selectedDate);

                                    // Don't trigger if clicking on an appointment block
                                    if (e.target.closest('.appointment-block')) {
                                      console.log('Click was on appointment block, ignoring');
                                      return;
                                    }

                                    // Don't trigger if clicking on the test div
                                    if (e.target.style && e.target.style.border && e.target.style.border.includes('dashed')) {
                                      console.log('Click was on test div, ignoring');
                                      return;
                                    }

                                    if (hasEditOrHigher && !isDateInPast(selectedDate)) {
                                      console.log('Conditions met, calling handleEmptySlotClick');
                                      handleEmptySlotClick(e, employee, selectedDate);
                                    } else {
                                      console.log('Conditions NOT met - hasEditOrHigher:', hasEditOrHigher, 'isDateInPast:', isDateInPast(selectedDate));
                                    }
                                  }}
                                  onMouseDown={(e) => {
                                    console.log('=== STAFF APPOINTMENTS MOUSE DOWN ===');
                                    console.log('Mouse down on staff-appointments');
                                    console.log('Event:', e);
                                    console.log('Target:', e.target);
                                    console.log('Target classList:', e.target.classList);
                                    console.log('Is appointment block?', e.target.closest('.appointment-block'));
                                    console.log('Is test div?', e.target.style && e.target.style.border && e.target.style.border.includes('dashed'));

                                    // If clicking on empty space (not appointment block, not test div), trigger click manually
                                    if (!e.target.closest('.appointment-block') &&
                                      !(e.target.style && e.target.style.border && e.target.style.border.includes('dashed'))) {
                                      console.log('Mouse down on empty space - will trigger click on mouse up');
                                      // Store the event to trigger click on mouse up
                                      e.currentTarget._pendingClick = true;
                                    }
                                  }}
                                  onMouseUp={(e) => {
                                    console.log('=== STAFF APPOINTMENTS MOUSE UP ===');
                                    console.log('Mouse up on staff-appointments');
                                    console.log('Target:', e.target);
                                    console.log('Is appointment block?', e.target.closest('.appointment-block'));
                                    console.log('Is test div?', e.target.style && e.target.style.border && e.target.style.border.includes('dashed'));

                                    // If clicking on empty space (not appointment block, not test div), handle it directly
                                    if (!e.target.closest('.appointment-block') &&
                                      !(e.target.style && e.target.style.border && e.target.style.border.includes('dashed')) &&
                                      hasEditOrHigher &&
                                      !isDateInPast(selectedDate)) {
                                      console.log('Mouse up on empty space - calling handleEmptySlotClick directly');
                                      // Call handleEmptySlotClick directly since onClick isn't firing
                                      handleEmptySlotClick(e, employee, selectedDate);
                                    }
                                  }}
                                  onPointerDown={(e) => {
                                    console.log('=== STAFF APPOINTMENTS POINTER DOWN ===');
                                    console.log('Pointer down on staff-appointments');
                                  }}
                                  title={hasEditOrHigher && !isDateInPast(selectedDate) ? "Click to add booking at this time" : ""}
                                >
                                  {/* Transparent overlay to capture clicks on empty space */}
                                  <div
                                    ref={(el) => {
                                      if (el && empIndex === 0) {
                                        console.log('=== OVERLAY ELEMENT RENDERED ===');
                                        console.log('Overlay element:', el);
                                        console.log('Overlay computed style:', window.getComputedStyle(el));
                                        console.log('Overlay offsetHeight:', el.offsetHeight);
                                        console.log('Overlay offsetWidth:', el.offsetWidth);
                                        console.log('Parent height:', el.parentElement?.offsetHeight);
                                      }
                                    }}
                                    style={{
                                      position: 'absolute',
                                      top: '0px',
                                      left: '0px',
                                      right: '0px',
                                      height: `${(24 - startHour) * slotHeight}px`, // Explicit height to match parent
                                      zIndex: 1,
                                      pointerEvents: 'auto',
                                      backgroundColor: 'transparent'
                                    }}
                                    onMouseEnter={(e) => {
                                      console.log('=== OVERLAY MOUSE ENTER ===', employee.employeeName);
                                      console.log('Event target:', e.target);
                                      console.log('Event currentTarget:', e.currentTarget);
                                    }}
                                    onMouseLeave={() => {
                                      console.log('=== OVERLAY MOUSE LEAVE ===', employee.employeeName);
                                    }}
                                    onMouseDown={(e) => {
                                      console.log('=== OVERLAY MOUSE DOWN ===');
                                      console.log('Target:', e.target);
                                      console.log('CurrentTarget:', e.currentTarget);
                                      console.log('Target === CurrentTarget?', e.target === e.currentTarget);
                                      console.log('Is appointment block?', e.target.closest('.appointment-block'));
                                      console.log('Is test div?', e.target.style && e.target.style.border && e.target.style.border.includes('dashed'));

                                      // Only handle if clicking directly on overlay (empty space)
                                      if (e.target === e.currentTarget ||
                                        (!e.target.closest('.appointment-block') &&
                                          !(e.target.style && e.target.style.border && e.target.style.border.includes('dashed')))) {
                                        console.log('Overlay mouse down on empty space');
                                        e.stopPropagation();
                                      } else {
                                        console.log('Overlay mouse down on appointment/test div - allowing through');
                                      }
                                    }}
                                    onMouseUp={(e) => {
                                      console.log('=== OVERLAY MOUSE UP ===');
                                      console.log('Target:', e.target);
                                      console.log('CurrentTarget:', e.currentTarget);
                                      console.log('Target === CurrentTarget?', e.target === e.currentTarget);
                                      console.log('Is appointment block?', e.target.closest('.appointment-block'));
                                      console.log('Is test div?', e.target.style && e.target.style.border && e.target.style.border.includes('dashed'));

                                      // Only handle if clicking directly on overlay (empty space)
                                      if (e.target === e.currentTarget ||
                                        (!e.target.closest('.appointment-block') &&
                                          !(e.target.style && e.target.style.border && e.target.style.border.includes('dashed')))) {
                                        console.log('=== OVERLAY MOUSE UP ON EMPTY SPACE ===');
                                        console.log('Calling handleEmptySlotClick from overlay');

                                        if (hasEditOrHigher && !isDateInPast(selectedDate)) {
                                          handleEmptySlotClick(e, employee, selectedDate);
                                        } else {
                                          console.log('Permissions not met - hasEditOrHigher:', hasEditOrHigher, 'isDateInPast:', isDateInPast(selectedDate));
                                        }
                                        e.stopPropagation();
                                      } else {
                                        console.log('Overlay mouse up on appointment/test div - allowing through');
                                      }
                                    }}
                                    onClick={(e) => {
                                      console.log('=== OVERLAY CLICK ===');
                                      console.log('Target:', e.target);
                                      console.log('CurrentTarget:', e.currentTarget);
                                    }}
                                  />
                                  {employee.items.map((item, itemIndex) => {
                                    const top = calculateTimePosition(item.fromTime, startHour, slotHeight);
                                    const height = calculateDuration(item.fromTime, item.toTime, slotHeight);
                                    const color = getAppointmentColor(item.detail, item.resource, item.completionStatus);

                                    // Calculate z-index based on time (later appointments on top)
                                    // Use higher z-index to be above the overlay (which is z-index 1)
                                    const zIndex = itemIndex + 20;

                                    return (
                                      <div
                                        key={item.id}
                                        className="appointment-block"
                                        style={{
                                          position: 'absolute',
                                          top: `${top}px`,
                                          height: `${height}px`,
                                          backgroundColor: color,
                                          border: `1px solid ${color.replace('0.8', '1').replace('fc', 'e0').replace('c4', 'd4').replace('c9', 'a5').replace('cf', '81')}`,
                                          minHeight: '28px',
                                          width: 'calc(100% - 4px)',
                                          margin: '0 2px',
                                          borderRadius: '6px',
                                          padding: '4px 6px',
                                          cursor: 'pointer',
                                          boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                          zIndex: zIndex,
                                          overflow: 'hidden',
                                          transition: 'all 0.2s ease',
                                          pointerEvents: 'auto' // Ensure appointment blocks are clickable
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation(); // Prevent click from bubbling to parent
                                          handleShowIndividualBooking(item.booking);
                                        }}
                                        title={`${formatTime(item.fromTime)} - ${formatTime(item.toTime)}\n${item.customerName}\n${item.entityName}\n${item.roomName}`}
                                      >
                                        {(() => {
                                          const textColor = getTextColor(color);
                                          const isVibrant = textColor === 'white';
                                          return (
                                            <>
                                              <div className="appointment-time" style={{ fontSize: '8.5px', fontWeight: '600', color: textColor, marginBottom: '0px', textShadow: isVibrant ? '0 1px 2px rgba(0,0,0,0.3)' : 'none' }}>
                                                {formatTime(item.fromTime)} - {formatTime(item.toTime)}
                                              </div>
                                              <div className="appointment-customer" style={{ fontSize: '10px', fontWeight: '600', color: textColor, marginBottom: '0px', textShadow: isVibrant ? '0 1px 2px rgba(0,0,0,0.3)' : 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {item.customerName}
                                              </div>
                                              <div className="appointment-service" style={{ fontSize: '9px', color: isVibrant ? 'rgba(255,255,255,0.95)' : '#6c757d', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textShadow: isVibrant ? '0 1px 2px rgba(0,0,0,0.2)' : 'none' }}>
                                                {item.entityName}
                                              </div>
                                              {item.roomName !== 'No Room' && (
                                                <div className="appointment-room" style={{ fontSize: '9px', color: isVibrant ? 'rgba(255,255,255,0.9)' : '#6c757d', marginTop: '2px', textShadow: isVibrant ? '0 1px 2px rgba(0,0,0,0.2)' : 'none' }}>
                                                  <MapPin size={8} style={{ display: 'inline', marginRight: '2px', color: isVibrant ? 'rgba(255,255,255,0.9)' : '#6c757d' }} />
                                                  {item.roomName}
                                                </div>
                                              )}
                                              <div className="appointment-status" style={{ fontSize: '9px', marginTop: '2px' }}>
                                                <span className="badge" style={{
                                                  backgroundColor: isVibrant ? 'rgba(255,255,255,0.25)' : 'rgba(73, 80, 87, 0.15)',
                                                  color: textColor,
                                                  fontSize: '8px',
                                                  padding: '2px 4px',
                                                  border: 'none',
                                                  textShadow: isVibrant ? '0 1px 2px rgba(0,0,0,0.3)' : 'none'
                                                }}>
                                                  {item.completionStatus || 'Pending'}
                                                </span>
                                              </div>
                                            </>
                                          );
                                        })()}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Current Time Indicator Line - positioned to span all columns */}
                        {currentTimePosition !== null && (
                          <div
                            className="current-time-indicator"
                            style={{
                              position: 'absolute',
                              top: `${currentTimePosition}px`,
                              left: '80px', // Start after time column
                              right: '0px',
                              height: '2px',
                              backgroundColor: '#dc3545',
                              zIndex: 100,
                              pointerEvents: 'none',
                              boxShadow: '0 0 4px rgba(220, 53, 69, 0.5)'
                            }}
                          >
                            <div
                              style={{
                                position: 'absolute',
                                left: '-80px',
                                top: '-6px',
                                width: '70px',
                                height: '14px',
                                backgroundColor: '#dc3545',
                                color: 'white',
                                fontSize: '10px',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '3px',
                                padding: '0 4px',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {currentTime.toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </ModalBody>
      </Modal>
    );
  };

  const renderCalendar = () => {
    const year = selectedMonth.getFullYear();
    const month = selectedMonth.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();

    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];

    const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    const calendarDays = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      calendarDays.push(<td key={`empty-${i}`} className="text-muted"></td>);
    }

    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const bookingCount = calendarData[dateKey] || 0;
      const isToday = isDateToday(dateKey);
      const isPast = isDateInPast(dateKey);

      let densityClass = 'density-none';
      if (bookingCount >= 15) {
        densityClass = 'density-high';
      } else if (bookingCount >= 7) {
        densityClass = 'density-medium';
      } else if (bookingCount >= 1) {
        densityClass = 'density-low';
      }

      calendarDays.push(
        <td
          key={day}
          className={`calendar-day-tile ${densityClass} ${isToday ? 'current-date-tile' : ''} ${isPast ? 'past-date-tile' : ''}`}
          onClick={() => handleDateClick(dateKey)}
          style={{ cursor: 'pointer' }}
        >
          <div className="calendar-tile-content">
            <div className="tile-top">
              <span className="calendar-date-number">{day}</span>
            </div>
            
            <div className="tile-center">
              {bookingCount > 0 ? (
                <span className="booking-status-text">
                  {bookingCount} {bookingCount === 1 ? 'booking' : 'bookings'}
                </span>
              ) : (
                <span className="booking-status-text empty">No bookings</span>
              )}
            </div>

            <div className="tile-bottom">
              {hasEditOrHigher && !isPast && (
                <button
                  className="quick-add-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddBooking(dateKey);
                  }}
                  title={`Add booking for ${dateKey}`}
                >
                  <Plus size={14} />
                </button>
              )}
            </div>
          </div>
        </td>
      );
    }

    // Create rows of 7 days each
    const rows = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      rows.push(
        <tr key={`row-${i}`}>
          {calendarDays.slice(i, i + 7)}
        </tr>
      );
    }

    return (
      <div className="calendar-container">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">{monthNames[month]} {year}</h4>
          <div>
            <Button
              color="outline-secondary"
              size="sm"
              onClick={() => handleMonthChange(new Date(year, month - 1))}
              className="me-2"
            >
              &lt; Previous
            </Button>
            <Button
              color="outline-secondary"
              size="sm"
              onClick={() => handleMonthChange(new Date(year, month + 1))}
            >
              Next &gt;
            </Button>
          </div>
        </div>

        <div className="table-responsive">
          <table className="calendar-table">
            <thead>
              <tr>
                {daysOfWeek.map(day => (
                  <th key={day} className="text-center bg-light">{day}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (!hasPermission) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center mt-5">
            <h3>Access Denied</h3>
            <p>You don't have permission to access this page.</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Spa Calendar" breadcrumbItem="Spa" />

        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader>
                <div className="d-flex justify-content-between align-items-center">
                  <h4 className="card-title mb-0">
                    <Calendar className="me-2" size={20} />
                    Spa Calendar
                  </h4>
                  {hasEditOrHigher && (
                    <Button
                      color="outline-primary"
                      size="sm"
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      <Filter className="me-1" size={16} />
                      {showFilters ? 'Hide' : 'Show'} Filters
                    </Button>
                  )}
                </div>
              </CardHeader>

              <CardBody>
                {/* View-only user message */}
                {isViewOnly && (
                  <div className="alert alert-info mb-3">
                    <i className="bx bx-info-circle me-2"></i>
                    You are viewing your own bookings only. Contact your administrator for additional access.
                  </div>
                )}

                {/* Filters Section - Only show for users with edit or higher permissions */}
                {hasEditOrHigher && showFilters && (
                  <Card className="mb-4">
                    <CardBody>
                      <Row>
                        <Col md={3}>
                          <FormGroup>
                            <Label for="customer">Customer</Label>
                            <Select
                              id="customer"
                              value={selectedCustomer}
                              onChange={setSelectedCustomer}
                              options={customers}
                              isClearable
                              placeholder="Select Customer"
                              styles={selectStyles}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={3}>
                          <FormGroup>
                            <Label for="employee">Employee</Label>
                            <Select
                              id="employee"
                              value={selectedEmployee}
                              onChange={setSelectedEmployee}
                              options={employees}
                              isClearable
                              placeholder="Select Employee"
                              styles={selectStyles}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={3}>
                          <FormGroup>
                            <Label for="month">Month</Label>
                            <Input
                              id="month"
                              type="month"
                              value={`${selectedMonth.getFullYear()}-${String(selectedMonth.getMonth() + 1).padStart(2, '0')}`}
                              onChange={(e) => {
                                const [year, month] = e.target.value.split('-');
                                const newDate = new Date(parseInt(year), parseInt(month) - 1);
                                console.log('Month input changed:', e.target.value, '->', newDate);
                                handleMonthChange(newDate);
                              }}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={3} className="d-flex align-items-end">
                          <div className="d-flex gap-2">
                            <Button
                              color="primary"
                              onClick={handleApplyFilter}
                              disabled={loading}
                            >
                              {loading ? (
                                <ClipLoader size={16} color="#fff" />
                              ) : (
                                <>
                                  <Eye className="me-1" size={16} />
                                  Apply Filter
                                </>
                              )}
                            </Button>
                            <Button
                              color="outline-secondary"
                              onClick={handleClearFilter}
                              disabled={loading}
                            >
                              Clear
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                )}

                {/* Calendar Section */}
                <div className="calendar-wrapper">
                  {loading ? (
                    <div className="text-center py-5">
                      <ClipLoader size={50} color="#556ee6" />
                      <p className="mt-3">Loading calendar data...</p>
                    </div>
                  ) : (
                    renderCalendar()
                  )}
                </div>


              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Booking Details Modal */}
      {renderBookingModal()}

      {/* Individual Booking Details Modal */}
      <Modal
        isOpen={showIndividualBookingModal}
        toggle={() => setShowIndividualBookingModal(false)}
        size="md"
        className="individual-booking-modal"
      >
        <ModalHeader toggle={() => setShowIndividualBookingModal(false)}>
          <div className="d-flex align-items-center">
            <Eye className="me-2" size={20} />
            <span>Booking Details</span>
          </div>
        </ModalHeader>
        <ModalBody>
          {selectedBooking && (
            <div>
              <div className="mb-4">
                <h6 className="text-primary mb-3">Time & Basic Info</h6>
                <div className="d-flex align-items-center mb-3">
                  <Clock className="me-2" size={16} />
                  <strong>{formatTime(selectedBooking.from_Time)} - {formatTime(selectedBooking.to_Time)}</strong>
                </div>
                <div className="d-flex align-items-center mb-2">
                  <User className="me-2" size={16} />
                  <span><strong>Customer:</strong> {selectedBooking.customer_Name}</span>
                </div>
                <div className="d-flex align-items-center mb-2">
                  <User className="me-2" size={16} />
                  <span><strong>Employee:</strong> {selectedBooking.employee_Name}</span>
                </div>
                <div className="d-flex align-items-center mb-3">
                  <MapPin className="me-2" size={16} />
                  <span><strong>Room:</strong> {selectedBooking.room_Name}</span>
                </div>
              </div>

              <div>
                <h6 className="text-primary mb-3">Services</h6>
                {selectedBooking.details && selectedBooking.details.length > 0 ? (
                  <div className="d-flex flex-column gap-3">
                    {selectedBooking.details.map((detail, detailIndex) => (
                      <div key={detailIndex} className="entity-group">
                        <h6 className="text-secondary mb-2">{getEntityTypeDisplay(detail.entity_Type)}: {detail.entity_Name}</h6>
                        {detail.resourceDetails && detail.resourceDetails.length > 0 ? (
                          <div className="resource-list">
                            {detail.resourceDetails.map((resource, resourceIndex) => {
                              const resourceStatus = resource.completionStatus || 'Pending';
                              const isPending = resourceStatus === 'Pending';
                              const isCheckedIn = resourceStatus === 'Check In';
                              const isStart = resourceStatus === 'Start';
                              const isStop = resourceStatus === 'Stop';
                              const isComplete = resourceStatus === 'Complete';

                              // Check if user can update:
                              // - Admin: can update all
                              // - View-only users with employee ID: can update only their own bookings
                              // - Users with view or higher permissions (not view-only): can update all bookings
                              let canUpdate = false;
                              if (isAdmin) {
                                canUpdate = true;
                              } else if (isViewOnly && currentEmployeeId) {
                                // View-only users with employee ID can only update their own bookings
                                canUpdate = resource.employee_Id === currentEmployeeId;
                              } else if (hasPermission) {
                                // Users with view permission (and not view-only, or view-only without employee ID)
                                // can update all bookings
                                canUpdate = true;
                              } else {
                                // Fallback: only if assigned to current user
                                canUpdate = resource.employee_Id === currentEmployeeId;
                              }

                              // Check if booking date is in the past
                              let isPastDate = false;
                              if (selectedBooking && selectedBooking.booking_Date) {
                                const bookingDate = new Date(selectedBooking.booking_Date);
                                const dateKey = `${bookingDate.getFullYear()}-${String(bookingDate.getMonth() + 1).padStart(2, '0')}-${String(bookingDate.getDate()).padStart(2, '0')}`;
                                isPastDate = isDateInPast(dateKey);
                              }

                              return (
                                <div key={resourceIndex} className="resource-item">
                                  <div className="d-flex justify-content-between align-items-center">
                                    <div className="flex-grow-1">
                                      <div className="d-flex align-items-center mb-1">
                                        <User className="me-2" size={14} />
                                        <span className="text-muted small">
                                          Employee: {
                                            !resource.employee_Id ? (
                                              <span style={{ color: '#dc3545', fontWeight: '500' }}>Not assigned</span>
                                            ) : (() => {
                                              // Get employee name from the global employee map
                                              const employeeMap = {};
                                              if (window.employeeData) {
                                                window.employeeData.forEach(e => employeeMap[e.employee_Id] = e.employee_NameEn || e.employee_Name);
                                              }
                                              return employeeMap[resource.employee_Id] || `Employee #${resource.employee_Id}`;
                                            })()
                                          }
                                        </span>
                                      </div>
                                      <div className="d-flex align-items-center mb-1">
                                        <Clock className="me-2" size={14} />
                                        <span className="text-muted small">
                                          {formatTime(resource.from_Time)} - {formatTime(resource.to_Time)}
                                        </span>
                                      </div>
                                      <div className="d-flex align-items-center mb-1">
                                        <span className="text-muted small">
                                          Status: <span className={`badge ${isComplete ? 'bg-success' :
                                            isStop ? 'bg-info' :
                                              isStart ? 'bg-warning' :
                                                isCheckedIn ? 'bg-primary' :
                                                  'bg-secondary'
                                            } text-white`} style={{ fontSize: '0.7rem' }}>
                                            {resourceStatus}
                                          </span>
                                        </span>
                                      </div>
                                    </div>
                                    <div className="ms-2">
                                      {canUpdate && !isPastDate && !isComplete && (
                                        <>
                                          {/* Check In Button - Show when status is Pending */}
                                          {isPending && (
                                            <Button
                                              size="sm"
                                              color="primary"
                                              onClick={() => handleCompletionStatusUpdate(
                                                resource.bookingResourceDetail_Id,
                                                'Check In',
                                                resource
                                              )}
                                              className="btn-sm"
                                              title="Check In"
                                              style={{ fontSize: '11px', padding: '4px 8px' }}
                                            >
                                              Check In
                                            </Button>
                                          )}

                                          {/* Start Button - Show when status is Check In */}
                                          {isCheckedIn && (
                                            <Button
                                              size="sm"
                                              color="warning"
                                              onClick={() => handleCompletionStatusUpdate(
                                                resource.bookingResourceDetail_Id,
                                                'Start',
                                                resource
                                              )}
                                              className="btn-sm"
                                              title="Start"
                                              style={{ fontSize: '11px', padding: '4px 8px' }}
                                            >
                                              Start
                                            </Button>
                                          )}

                                          {/* Stop Button - Show when status is Start */}
                                          {isStart && (
                                            <Button
                                              size="sm"
                                              color="info"
                                              onClick={() => handleCompletionStatusUpdate(
                                                resource.bookingResourceDetail_Id,
                                                'Stop',
                                                resource
                                              )}
                                              className="btn-sm"
                                              title="Stop"
                                              style={{ fontSize: '11px', padding: '4px 8px' }}
                                            >
                                              Stop
                                            </Button>
                                          )}

                                          {/* No button shown when status is Stop */}
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <span className="text-muted small">No resource details</span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="text-muted">No details</span>
                )}
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowIndividualBookingModal(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Poppins:wght@400;500;600&display=swap');

        .calendar-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 6px; /* Reduced spacing to save vertical space */
          background: transparent;
          font-family: 'Inter', 'Poppins', -apple-system, sans-serif;
        }
        
        .calendar-table th {
          text-transform: uppercase;
          font-size: 11px;
          font-weight: 600;
          color: #6B7280; /* Darker header text */
          letter-spacing: 0.1em;
          padding: 8px 0; /* Reduced padding */
          border: none !important;
          background-color: transparent !important;
          text-align: center;
        }

        .calendar-day-tile {
          height: 90px; /* Reduced height */
          min-width: 110px; /* Reduced width */
          vertical-align: top;
          padding: 10px !important; /* Reduced padding */
          border-radius: 10px;
          border: 1px solid #D1D5DB !important; /* Slightly darker border for better definition */
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          position: relative;
        }

        .calendar-day-tile:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
        }

        .calendar-tile-content {
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: space-between;
        }

        .calendar-date-number {
          font-size: 16px;
          font-weight: 600;
          color: #111827;
        }

        .tile-center {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
        }

        .booking-status-text {
          font-size: 14px;
          font-weight: 600; /* Made bolder */
          color: #1F2937; /* Darker text */
        }

        .booking-status-text.empty {
          color: #9CA3AF;
          font-weight: 400;
          opacity: 0.5;
          display: none; /* Hide "No bookings" to keep it clean, as per "3 elements only" rule */
        }

        .tile-bottom {
          display: flex;
          justify-content: center;
          width: 100%;
        }

        .quick-add-btn {
          width: 26px;
          height: 26px;
          border-radius: 6px;
          border: 1px solid #D1D5DB; /* Darker border */
          background: #ffffff;
          color: #4B5563; /* Darker icon */
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          transition: all 0.2s ease;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }

        .quick-add-btn:hover {
          background: #3B82F6;
          color: white;
          border-color: #3B82F6;
        }

        /* Density Colors - Darkened for better contrast */
        .density-none { background-color: #ffffff !important; }
        .density-low { background-color: #f3f4f6 !important; }
        .density-medium { background-color: #dbeafe !important; }
        .density-high { background-color: #bfdbfe !important; }

        .current-date-tile {
          border: 2px solid #3B82F6 !important;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
        }
        
        .past-date-tile {
          opacity: 0.6;
        }

        .calendar-wrapper {
          padding: 10px 0;
          min-height: 400px;
        }

        /* Grid Schedule Styles */
        .daily-schedule-grid-container {
          width: 100%;
          display: flex;
          flex-direction: column;
          height: 100%;
          flex: 1;
          min-height: 0;
          overflow: hidden;
        }

        .schedule-grid-wrapper {
          overflow-x: hidden;
          overflow-y: hidden;
          -webkit-overflow-scrolling: touch;
          flex: 1 1 auto;
          position: relative;
          min-height: 0;
          height: 0;
          isolation: isolate;
          display: flex;
          flex-direction: column;
        }

        .schedule-grid {
          display: flex;
          min-width: fit-content;
          border: 1px solid #dee2e6;
          background: white;
          align-items: stretch;
          position: relative;
        }

        .schedule-grid-header-row {
          flex-shrink: 0;
          border-bottom: 0;
        }

        .schedule-grid-header-scroll {
          flex-shrink: 0;
          overflow-x: hidden;
        }

        .schedule-grid-body-scroll {
          overflow-y: auto;
          overflow-x: auto;
          min-height: 0;
          flex: 1 1 auto;
          position: relative;
          scrollbar-width: thin;
          scrollbar-color: #888 #f1f1f1;
        }

        .schedule-grid-body-scroll::-webkit-scrollbar {
          height: 8px;
        }

        .schedule-grid-body-scroll::-webkit-scrollbar-track {
          background: #f1f1f1;
        }

        .schedule-grid-body-scroll::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 4px;
        }

        .schedule-grid-body-scroll::-webkit-scrollbar-thumb:hover {
          background: #555;
        }

        .schedule-grid-body-scroll .schedule-grid {
          border-top: 0;
        }

        .time-column {
          position: -webkit-sticky;
          position: sticky;
          left: 0;
          z-index: 60;
          background: white;
          border-right: 2px solid #dee2e6;
          min-width: 80px;
          display: flex;
          flex-direction: column;
          flex-shrink: 0;
        }

        .time-column .time-header {
          position: sticky;
          top: 0;
          left: 0;
          z-index: 70;
          background: #f8f9fa;
        }

        .time-header {
          height: 100px;
          border-bottom: 2px solid #dee2e6;
          background: #f8f9fa;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 12px;
          color: #495057;
          flex-shrink: 0;
          position: sticky;
          top: 0;
          z-index: 35;
        }

        .time-slots {
          position: relative;
          flex: 1;
        }

        .time-slot-cell {
          border-bottom: 1px solid #e9ecef;
          display: flex;
          align-items: flex-start;
          padding: 2px 4px;
        }

        .time-label {
          font-size: 11px;
          color: #6c757d;
          font-weight: 500;
        }

        .staff-columns {
          display: flex;
          flex: 1;
          position: relative;
          align-items: flex-start;
        }

        .staff-column {
          min-width: 200px;
          max-width: 200px;
          border-right: 1px solid #e9ecef;
          display: flex;
          flex-direction: column;
          flex-shrink: 0;
          align-items: stretch;
          position: relative;
        }

        .staff-header {
          height: 100px;
          border-bottom: 2px solid #dee2e6;
          background: #f8f9fa !important;
          padding: 12px 8px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 8px;
          flex-shrink: 0;
          position: relative;
          top: auto;
          z-index: 50;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          width: 100%;
        }

        .staff-avatar {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          background: #556ee6;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 24px;
          cursor: help;
          transition: all 0.2s ease;
          position: relative;
          overflow: hidden;
          flex-shrink: 0;
        }

        .staff-avatar:hover {
          background: #4054b2;
          transform: scale(1.1);
        }

        .clickable-avatar {
          cursor: pointer !important;
        }

        .clickable-avatar:hover {
          background: #4054b2 !important;
          transform: scale(1.1);
          box-shadow: 0 4px 8px rgba(0,0,0,0.2);
        }

        .staff-avatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 50%;
        }

        .staff-name {
          font-size: 12px;
          font-weight: 600;
          color: #495057;
          text-align: center;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 100%;
          cursor: help;
          line-height: 1.2;
        }

        .staff-count {
          font-size: 9px;
          color: #6c757d;
          background: #e9ecef;
          padding: 2px 6px;
          border-radius: 10px;
          cursor: help;
          transition: all 0.2s ease;
        }

        .staff-count:hover {
          background: #dee2e6;
          transform: scale(1.1);
        }

        .staff-appointments {
          position: relative;
          flex: 1;
          background: repeating-linear-gradient(
            to bottom,
            transparent,
            transparent 59px,
            #f8f9fa 59px,
            #f8f9fa 60px
          );
          pointer-events: auto !important;
          z-index: 1;
        }
        
        /* Ensure overlay can receive events */
        .staff-appointments > div[style*="zIndex: 1"] {
          pointer-events: auto !important;
        }
        
        .staff-appointments:hover {
          background: repeating-linear-gradient(
            to bottom,
            rgba(85, 110, 230, 0.05),
            rgba(85, 110, 230, 0.05) 59px,
            rgba(85, 110, 230, 0.08) 59px,
            rgba(85, 110, 230, 0.08) 60px
          );
        }

        .appointment-block {
          transition: all 0.2s ease;
          pointer-events: auto;
          z-index: 10;
        }
        
        /* Ensure staff-appointments can receive clicks */
        .staff-appointments {
          pointer-events: auto !important;
        }
        
        .staff-appointments * {
          pointer-events: auto;
        }

        .appointment-block:hover {
          transform: scale(1.15);
          box-shadow: 0 8px 25px rgba(0,0,0,0.6) !important;
          z-index: 1000 !important;
          overflow: visible !important;
          height: auto !important;
          min-height: fit-content !important;
          padding: 8px 10px !important;
          border-width: 2px !important;
          opacity: 1 !important;
        }

        .appointment-block:hover * {
          white-space: normal !important;
          overflow: visible !important;
          text-overflow: clip !important;
          margin-bottom: 4px !important;
        }

        .appointment-block:hover .appointment-time,
        .appointment-block:hover .appointment-customer,
        .appointment-block:hover .appointment-service {
          font-size: 11px !important;
        }

        .current-time-indicator {
          transition: top 0.3s ease;
        }

        .current-time-indicator::before {
          content: '';
          position: absolute;
          left: -4px;
          top: -3px;
          width: 8px;
          height: 8px;
          background-color: #dc3545;
          border-radius: 50%;
          box-shadow: 0 0 4px rgba(220, 53, 69, 0.8);
        }

        .appointment-time {
          white-space: nowrap;
        }

        .appointment-customer {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .appointment-service {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* Legacy timeline styles (kept for backward compatibility) */
        .schedule-timeline {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .schedule-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          background-color: #f8f9fa;
          border-radius: 8px;
          border-left: 4px solid #556ee6;
        }

        .clickable-booking-item {
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .clickable-booking-item:hover {
          background-color: #e9ecef;
          border-left-color: #4054b2;
          transform: translateX(2px);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .time-slot {
          display: flex;
          flex-direction: column;
          align-items: center;
          min-width: 120px;
          padding: 0.5rem;
          background-color: white;
          border-radius: 6px;
          border: 1px solid #dee2e6;
        }

        .time-slot .time {
          font-weight: 600;
          color: #556ee6;
        }

        .time-slot .separator {
          color: #6c757d;
          margin: 2px 0;
        }

        .booking-info {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .customer-name, .employee-name, .room-name {
          display: flex;
          align-items: center;
          font-size: 0.9rem;
        }

        .customer-name {
          font-weight: 600;
          color: #495057;
        }

        .employee-name {
          color: #6c757d;
        }

        .room-name {
          color: #6c757d;
        }

        .booking-details-modal .modal-lg,
        .booking-details-modal .modal-xl {
          max-width: 95vw !important;
          width: 95vw !important;
          max-height: 95vh !important;
          height: 95vh !important;
        }

        .booking-details-modal .modal-content {
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          max-height: 95vh;
        }

        .booking-details-modal .modal-body {
          padding: 0;
          flex: 1;
          min-height: 0;
          overflow: hidden;
        }

        .booking-details-modal .modal-header {
          flex-shrink: 0;
        }

        .booking-details-modal .modal-footer {
          flex-shrink: 0;
          padding: 8px 16px;
          min-height: auto;
          border-top: 1px solid #dee2e6;
        }

        .entity-item {
          padding: 8px 12px;
          background-color: #f8f9fa;
          border-radius: 6px;
          border-left: 3px solid #556ee6;
          margin-bottom: 4px;
        }

        .entity-item:last-child {
          margin-bottom: 0;
        }

        .entity-item small {
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .entity-item strong {
          font-size: 0.9rem;
          line-height: 1.2;
        }

        .entity-group {
          border: 1px solid #dee2e6;
          border-radius: 8px;
          padding: 16px;
          background-color: #f8f9fa;
        }

        .entity-group h6 {
          font-size: 0.9rem;
          font-weight: 600;
          color: #495057;
          margin-bottom: 12px;
          padding-bottom: 8px;
          border-bottom: 2px solid #556ee6;
        }

        .entity-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .entity-list .entity-item {
          margin-bottom: 0;
          background-color: white;
          border-left: 3px solid #556ee6;
        }

        .resource-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .resource-item {
          padding: 12px;
          background-color: #f8f9fa;
          border-radius: 6px;
          border: 1px solid #dee2e6;
        }

        .resource-item:hover {
          background-color: #e9ecef;
        }

        .cursor-pointer {
          cursor: pointer;
        }

        .cursor-pointer:hover {
          background-color: #f8f9fa;
        }

        .booking-actions {
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 40px;
        }

        .booking-actions .btn {
          transition: all 0.2s ease;
        }

        .booking-actions .btn:hover {
          transform: scale(1.1);
        }
      `}</style>
    </div>
  );
};

export default SpaCalendarPage;
